import React, { useState } from 'react'
import { AlertTriangle, Check, X, RefreshCw } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'

interface ConflictField {
  field: string
  label: string
  yours: string
  theirs: string
  resolution?: 'yours' | 'theirs' | 'custom'
  customValue?: string
}

interface BlogConflictResolverProps {
  conflicts: ConflictField[]
  onResolve: (resolutions: ConflictField[]) => void
  onCancel: () => void
  isOpen: boolean
}

export const BlogConflictResolver: React.FC<BlogConflictResolverProps> = ({
  conflicts,
  onResolve,
  onCancel,
  isOpen,
}) => {
  const [resolutions, setResolutions] = useState<ConflictField[]>(
    conflicts.map(c => ({ ...c, resolution: 'yours' }))
  )
  const [showPreview, setShowPreview] = useState(false)

  const handleResolutionChange = (index: number, resolution: 'yours' | 'theirs' | 'custom') => {
    const updated = [...resolutions]
    updated[index] = { ...updated[index], resolution }
    setResolutions(updated)
  }

  const handleCustomValueChange = (index: number, value: string) => {
    const updated = [...resolutions]
    updated[index] = { ...updated[index], customValue: value }
    setResolutions(updated)
  }

  const handleResolveAll = (choice: 'yours' | 'theirs') => {
    const updated = resolutions.map(r => ({ ...r, resolution: choice }))
    setResolutions(updated)
  }

  const handleSubmit = () => {
    onResolve(resolutions)
  }

  const getResolvedValue = (conflict: ConflictField) => {
    if (conflict.resolution === 'yours') return conflict.yours
    if (conflict.resolution === 'theirs') return conflict.theirs
    return conflict.customValue || conflict.yours
  }

  return (
    <Dialog open={isOpen} onOpenChange={() => onCancel()}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
              Resolve Edit Conflicts
            </div>
          </DialogTitle>
          <DialogDescription>
            Another user has modified this post. Please resolve the conflicts below.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Quick Actions */}
          <div className="flex gap-2 p-3 bg-muted rounded-lg">
            <Button
              size="sm"
              variant="outline"
              onClick={() => handleResolveAll('yours')}
            >
              <Check className="w-4 h-4 mr-1" />
              Keep All Mine
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => handleResolveAll('theirs')}
            >
              <RefreshCw className="w-4 h-4 mr-1" />
              Take All Theirs
            </Button>
          </div>

          {/* Conflicts List */}
          {resolutions.map((conflict, index) => (
            <Card key={index}>
              <CardHeader className="py-3">
                <CardTitle className="text-sm font-medium">
                  {conflict.label}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <RadioGroup
                  value={conflict.resolution}
                  onValueChange={(value) => handleResolutionChange(index, value as 'yours' | 'theirs')}
                >
                  {/* Your Version */}
                  <div className="flex items-start space-x-2 p-3 border border-border rounded-lg">
                    <RadioGroupItem value="yours" id={`yours-${index}`} />
                    <Label htmlFor={`yours-${index}`} className="flex-1 cursor-pointer">
                      <div>
                        <Badge variant="secondary" className="mb-2">Your Version</Badge>
                        <div className="text-sm text-foreground whitespace-pre-wrap">
                          {conflict.yours}
                        </div>
                      </div>
                    </Label>
                  </div>

                  {/* Their Version */}
                  <div className="flex items-start space-x-2 p-3 border border-border rounded-lg">
                    <RadioGroupItem value="theirs" id={`theirs-${index}`} />
                    <Label htmlFor={`theirs-${index}`} className="flex-1 cursor-pointer">
                      <div>
                        <Badge variant="secondary" className="mb-2">Their Version</Badge>
                        <div className="text-sm text-foreground whitespace-pre-wrap">
                          {conflict.theirs}
                        </div>
                      </div>
                    </Label>
                  </div>

                  {/* Custom Resolution */}
                  <div className="flex items-start space-x-2 p-3 border border-border rounded-lg">
                    <RadioGroupItem value="custom" id={`custom-${index}`} />
                    <Label htmlFor={`custom-${index}`} className="flex-1">
                      <div>
                        <Badge variant="secondary" className="mb-2">Custom</Badge>
                        <textarea
                          className="w-full p-2 text-sm border border-border rounded-md bg-background text-foreground"
                          rows={3}
                          placeholder="Enter custom value..."
                          value={conflict.customValue || ''}
                          onChange={(e) => handleCustomValueChange(index, e.target.value)}
                          onClick={() => handleResolutionChange(index, 'custom')}
                        />
                      </div>
                    </Label>
                  </div>
                </RadioGroup>
              </CardContent>
            </Card>
          ))}

          {/* Preview Button */}
          <Button
            variant="outline"
            onClick={() => setShowPreview(!showPreview)}
            className="w-full"
          >
            {showPreview ? 'Hide' : 'Show'} Resolution Preview
          </Button>

          {/* Resolution Preview */}
          {showPreview && (
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Resolution Preview</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {resolutions.map((conflict, index) => (
                    <div key={index} className="text-sm">
                      <span className="font-medium">{conflict.label}:</span>
                      <div className="ml-4 text-muted-foreground whitespace-pre-wrap">
                        {getResolvedValue(conflict)}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onCancel}>
            <X className="w-4 h-4 mr-1" />
            Cancel
          </Button>
          <Button onClick={handleSubmit}>
            <Check className="w-4 h-4 mr-1" />
            Apply Resolution
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}